import {
  Card,
  CardHeader,
  CardContent,
  Avatar,
  Typography,
  Grid,
  Box,
  Stack,
  Button,
} from '@mui/material';
import { Stethoscope, PencilSimple, ArrowRight } from '@phosphor-icons/react';
import { useState } from 'react';
import { Api } from '../api';
import { healthQuestionnaire } from '../questionnaires/healthQuestionnaire';
import { onboardingQuestionnaire } from '../questionnaires/onboardingQuestionnaire';
import SoloLoading from './SoloLoading';
import EditPregnancyFertilityModal from './modals/EditPregnancyFertilityModal';
import EditDiagnosisModal from './modals/EditDiagnosisModal';
import EditFrequentSymptomsModal from './modals/EditFrequentSymptomsModal';
import EditDietModal from './modals/EditDietModal';
import EditContraceptionModal from './modals/EditContraceptionModal';
import EditSurgeryModal from './modals/EditSurgeryModal';
import { useNavigate } from 'react-router';
import { Model } from '../models';
import { useUserStatusContext } from '../contexts';

interface HealthProfileSectionProps {
  stats: Api.UserStats.V;
}

interface QuestionnaireResponse {
  question: string;
  answer: string | string[] | number | Record<string, string>;
  questionSlug?: string;
  answerSlug?: string;
}

export default function HealthProfileSection({
  stats,
}: HealthProfileSectionProps) {
  const { hasEditAccess } = useUserStatusContext();
  const navigate = useNavigate();

  const userId = stats?.user.id;

  const questionnaireApi = Api.UserQuestionnaire.useReadItemQuestionnairesType(
    userId,
    'before_appointment',
    {
      staleWhileInvalidated: true,
    },
  );
  const onboardingQuestionnaireApi =
    Api.UserQuestionnaire.useReadItemQuestionnairesType(userId, 'onboarding', {
      staleWhileInvalidated: true,
    });

  const [pregnancyFertilityModalOpen, setPregnancyFertilityModalOpen] =
    useState(false);
  const [diagnosisModalOpen, setDiagnosisModalOpen] = useState(false);
  const [frequentSymptomsModalOpen, setFrequentSymptomsModalOpen] =
    useState(false);
  const [dietModalOpen, setDietModalOpen] = useState(false);
  const [contraceptionModalOpen, setContraceptionModalOpen] = useState(false);
  const [surgeryModalOpen, setSurgeryModalOpen] = useState(false);

  if (questionnaireApi.error || onboardingQuestionnaireApi.error) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body2" color="error">
            Failed to load health profile data.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!questionnaireApi.data || !onboardingQuestionnaireApi.data) {
    return (
      <Card>
        <CardHeader
          avatar={
            <Avatar>
              <Stethoscope size={24} />
            </Avatar>
          }
          title="Health Profile"
        />
        <CardContent>
          <SoloLoading />
        </CardContent>
      </Card>
    );
  }

  const healthResponses: QuestionnaireResponse[] =
    questionnaireApi.data?.responses?.map((r): QuestionnaireResponse => {
      if (Array.isArray(r)) {
        const [question, answer] = r;
        return { question, answer };
      }
      return r as QuestionnaireResponse;
    }) || [];

  const onboardingResponses: QuestionnaireResponse[] =
    onboardingQuestionnaireApi.data?.responses?.map(
      (r): QuestionnaireResponse => {
        if (Array.isArray(r)) {
          const [question, answer] = r;
          return { question, answer };
        }
        return r as QuestionnaireResponse;
      },
    ) || [];

  // Combine both responses for easy lookup
  const allResponses = [...healthResponses, ...onboardingResponses];

  const getResponseByQuestion = (
    questionText: string,
  ): string | string[] | number | Record<string, string> => {
    const response = allResponses.find((r) => r.question === questionText);
    return response?.answer || '-';
  };

  const getPregnancyFertilityData = () => {
    const pregnancySection = healthQuestionnaire.sections.find(
      (s) => s.id === 'pregnancy',
    );
    if (!pregnancySection) return {};

    const data: any = {};

    const onboardingQuestion = onboardingQuestionnaire.sections
      .flatMap((s) => s.questions)
      .find((q) => q.question === 'Are you currently looking to get pregnant?');

    if (onboardingQuestion && onboardingQuestion.question) {
      data.lookingToGetPregnant = getResponseByQuestion(
        onboardingQuestion.question,
      );
    }

    pregnancySection.questions.forEach((q) => {
      const response = getResponseByQuestion(q.question || '');
      switch (q.id) {
        case 'q32':
          data.pregnancyStatus = response;
          break;
        case 'q33':
          data.children = response;
          break;
        case 'q34':
          data.complications = response;
          break;
        case 'q35':
          data.art = response;
          break;
        case 'q36':
          data.artCycles =
            response && response !== '-' ? `${response} cycles` : '';
          break;
      }
    });
    return data;
  };

  const getSurgeryData = () => {
    const surgeryQuestion = healthQuestionnaire.sections
      .flatMap((s) => s.questions)
      .find((q) => q.id === 'q37');

    if (!surgeryQuestion) return '-';

    return formatDisplayText(
      getResponseByQuestion(surgeryQuestion.question || ''),
    );
  };

  const getMedicationData = () => {
    const medicationQuestion = Model.Questionnaire.getQuestion(
      'health',
      'health_conditions',
      'q38',
    );
    const medicationResponse = getResponseByQuestion(medicationQuestion);
    return Array.isArray(medicationResponse) ? medicationResponse : [];
  };

  const formatDisplayText = (
    value:
      | string
      | string[]
      | number
      | Record<string, string>
      | null
      | undefined,
  ): string => {
    if (!value || value === '-') return '-';
    if (typeof value === 'number') return String(value);
    if (typeof value === 'object' && !Array.isArray(value)) {
      return Object.entries(value)
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ');
    }

    // Handle array format (already split)
    if (Array.isArray(value)) {
      if (value.length === 0) return '-';
      if (value.length <= 3) {
        return value.join(', ');
      }
      return `${value.slice(0, 2).join(', ')} and ${value.length - 2} more`;
    }

    // Handle string format
    const text = String(value);
    const parts = text.includes(', ') ? text.split(', ') : [text];

    if (parts.length === 1) return text;
    if (parts.length <= 3) {
      return parts.join(', ');
    }

    return `${parts.slice(0, 2).join(', ')} and ${parts.length - 2} more`;
  };

  const pregnancyData = getPregnancyFertilityData();
  const diagnoses = stats?.user?.diagnoses || [];
  const generalHealthConditions =
    (stats?.user?.generalHealthConditions as unknown as string[]) || [];
  const medicationData = getMedicationData();
  const surgeryData = getSurgeryData();

  return (
    <>
      <Card>
        <CardHeader
          avatar={
            <Avatar>
              <Stethoscope size={24} />
            </Avatar>
          }
          title="Health Profile"
        />
        <CardContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#212636' }}>
            <span style={{ fontWeight: 600 }}>Please note: </span>
            Changes to a user's health profile may overwrite previously edited
            content tags. Review all content tags after updating the user's
            health profile to ensure the user receives the correct content.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Stack spacing={2}>
                <Box
                  sx={{
                    bgcolor: 'var(--mui-palette-background-level1)',
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="subtitle1">Diagnosis</Typography>
                      {hasEditAccess && (
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<PencilSimple size={16} />}
                          onClick={() => setDiagnosisModalOpen(true)}
                        >
                          Edit
                        </Button>
                      )}
                    </Stack>

                    <Stack spacing={1}>
                      <Typography variant="subtitle2">
                        Women Health Conditions
                      </Typography>
                      {diagnoses.length > 0 ? (
                        diagnoses.map((diagnosis, index) => (
                          <Typography
                            key={index}
                            variant="body2"
                            color="text.secondary"
                          >
                            {Model.User.getDiagnosisLabel(diagnosis)} -
                            diagnosed at {diagnosis.diagnosedAge}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </Stack>

                    <Stack spacing={1}>
                      <Typography variant="subtitle2">
                        Chronic Health Conditions
                      </Typography>
                      {generalHealthConditions.length > 0 ? (
                        generalHealthConditions.map((condition, index) => (
                          <Typography
                            key={index}
                            variant="body2"
                            color="text.secondary"
                          >
                            {condition}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Box>

                <Box
                  sx={{
                    bgcolor: 'var(--mui-palette-background-level1)',
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="subtitle1">
                        Frequent symptoms
                      </Typography>
                      {hasEditAccess && (
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<PencilSimple size={16} />}
                          onClick={() => setFrequentSymptomsModalOpen(true)}
                        >
                          Edit
                        </Button>
                      )}
                    </Stack>

                    <Stack spacing={1}>
                      {stats?.user?.frequentSymptoms &&
                      stats.user.frequentSymptoms.length > 0 ? (
                        stats.user.frequentSymptoms.map((symptom, index) => (
                          <Typography
                            key={index}
                            variant="body2"
                            color="text.secondary"
                          >
                            {symptom.name}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={4}>
              <Stack spacing={2}>
                <Box
                  sx={{
                    bgcolor: 'var(--mui-palette-background-level1)',
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="subtitle1">
                        Pregnancy and Fertility
                      </Typography>
                      {hasEditAccess && (
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<PencilSimple size={16} />}
                          onClick={() => setPregnancyFertilityModalOpen(true)}
                        >
                          Edit
                        </Button>
                      )}
                    </Stack>

                    {pregnancyData.lookingToGetPregnant &&
                      pregnancyData.lookingToGetPregnant !== '-' && (
                        <Stack spacing={1}>
                          <Typography variant="subtitle2">
                            Looking to get pregnant?
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatDisplayText(
                              pregnancyData.lookingToGetPregnant,
                            )}
                          </Typography>
                        </Stack>
                      )}

                    <Stack spacing={1}>
                      <Typography variant="subtitle2">
                        Have you ever been pregnant?
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatDisplayText(pregnancyData.pregnancyStatus)}
                      </Typography>
                    </Stack>

                    {pregnancyData.children &&
                      pregnancyData.children !== '-' &&
                      // Only show if has been pregnant and has children
                      (Array.isArray(pregnancyData.pregnancyStatus)
                        ? pregnancyData.pregnancyStatus.includes(
                            'I have been pregnant and have children',
                          )
                        : pregnancyData.pregnancyStatus ===
                          'I have been pregnant and have children') && (
                        <Stack spacing={1}>
                          <Typography variant="subtitle2">Children</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {pregnancyData.children}
                          </Typography>
                        </Stack>
                      )}

                    {pregnancyData.complications &&
                      pregnancyData.complications !== '-' &&
                      // Only show if has been pregnant
                      (Array.isArray(pregnancyData.pregnancyStatus)
                        ? pregnancyData.pregnancyStatus.some((status: string) =>
                            status.includes('I have been pregnant'),
                          )
                        : String(pregnancyData.pregnancyStatus).includes(
                            'I have been pregnant',
                          )) && (
                        <Stack spacing={1}>
                          <Typography variant="subtitle2">
                            Pregnancy-related losses or complications
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatDisplayText(pregnancyData.complications)}
                          </Typography>
                        </Stack>
                      )}

                    {pregnancyData.art && pregnancyData.art !== '-' && (
                      <Stack spacing={1}>
                        <Typography variant="subtitle2">ART</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatDisplayText(pregnancyData.art)}
                        </Typography>
                        {pregnancyData.artCycles &&
                          pregnancyData.artCycles !== '-' &&
                          // Only show cycles if not "No ART procedures"
                          !(Array.isArray(pregnancyData.art)
                            ? pregnancyData.art.includes(
                                "No, I haven't undergone any ART procedures",
                              )
                            : String(pregnancyData.art).includes(
                                "No, I haven't undergone any ART procedures",
                              )) && (
                            <Typography variant="body2" color="text.secondary">
                              {pregnancyData.artCycles}
                            </Typography>
                          )}
                      </Stack>
                    )}
                  </Stack>
                </Box>

                <Box
                  sx={{
                    bgcolor: 'var(--mui-palette-background-level1)',
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="subtitle1">Diet</Typography>
                      {hasEditAccess && (
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<PencilSimple size={16} />}
                          onClick={() => setDietModalOpen(true)}
                        >
                          Edit
                        </Button>
                      )}
                    </Stack>

                    <Stack spacing={1}>
                      {stats?.user?.recipePreferences &&
                      stats.user.recipePreferences.length > 0 ? (
                        stats.user.recipePreferences.map((recipe, index) => (
                          <Typography
                            key={index}
                            variant="body2"
                            color="text.secondary"
                          >
                            {recipe.name}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={4}>
              <Stack spacing={2}>
                <Box
                  sx={{
                    bgcolor: 'var(--mui-palette-background-level1)',
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="subtitle1">
                        Contraception & Medication
                      </Typography>
                      {hasEditAccess && (
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<PencilSimple size={16} />}
                          onClick={() => setContraceptionModalOpen(true)}
                        >
                          Edit
                        </Button>
                      )}
                    </Stack>

                    <Stack spacing={1}>
                      <Typography variant="subtitle2">
                        Current Contraception
                      </Typography>
                      {stats?.user?.contraceptions &&
                      stats.user.contraceptions.filter((c) => c.currentlyUsing)
                        .length > 0 ? (
                        stats.user.contraceptions
                          .filter((c) => c.currentlyUsing)
                          .map((contraception, index) => (
                            <Typography
                              key={index}
                              variant="body2"
                              color="text.secondary"
                            >
                              {Model.Questionnaire.getContraceptionLabel(
                                contraception.type,
                              )}
                            </Typography>
                          ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </Stack>

                    <Stack spacing={1}>
                      <Typography variant="subtitle2">
                        Contraception used in the past
                      </Typography>
                      {stats?.user?.contraceptions &&
                      stats.user.contraceptions.filter((c) => !c.currentlyUsing)
                        .length > 0 ? (
                        stats.user.contraceptions
                          .filter((c) => !c.currentlyUsing)
                          .map((contraception, index) => (
                            <Typography
                              key={index}
                              variant="body2"
                              color="text.secondary"
                            >
                              {Model.Questionnaire.getContraceptionLabel(
                                contraception.type,
                              )}
                            </Typography>
                          ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </Stack>

                    <Stack spacing={1}>
                      <Typography variant="subtitle2">
                        Current Medication
                      </Typography>
                      {medicationData.length > 0 ? (
                        medicationData.map((medication, index) => (
                          <Typography
                            key={index}
                            variant="body2"
                            color="text.secondary"
                          >
                            {Model.Questionnaire.getMedicationLabel(medication)}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Box>

                <Box
                  sx={{
                    bgcolor: 'var(--mui-palette-background-level1)',
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="subtitle1">
                        Surgery to reproductive organs
                      </Typography>
                      {hasEditAccess && (
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<PencilSimple size={16} />}
                          onClick={() => setSurgeryModalOpen(true)}
                        >
                          Edit
                        </Button>
                      )}
                    </Stack>

                    <Typography variant="body2" color="text.secondary">
                      {surgeryData}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
        <CardHeader
          sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}
          title={
            <Button
              variant="text"
              endIcon={<ArrowRight />}
              onClick={() =>
                navigate(`/users/${userId}/questionnaire-responses`)
              }
            >
              All Questionnaire Responses
            </Button>
          }
        />
      </Card>

      <EditPregnancyFertilityModal
        open={pregnancyFertilityModalOpen}
        onClose={() => setPregnancyFertilityModalOpen(false)}
        userId={userId}
        healthResponses={healthResponses}
        onboardingResponses={onboardingResponses}
      />

      <EditDiagnosisModal
        open={diagnosisModalOpen}
        onClose={() => setDiagnosisModalOpen(false)}
        userId={userId}
        existingDiagnoses={stats?.user?.diagnoses || []}
        existingGeneralHealthConditions={
          stats?.user?.generalHealthConditions || []
        }
      />

      <EditFrequentSymptomsModal
        open={frequentSymptomsModalOpen}
        onClose={() => setFrequentSymptomsModalOpen(false)}
        userId={userId}
        existingSymptoms={stats?.user?.frequentSymptoms || []}
      />

      <EditDietModal
        open={dietModalOpen}
        onClose={() => setDietModalOpen(false)}
        userId={userId}
        existingRecipePreferences={stats?.user?.recipePreferences || []}
      />

      <EditContraceptionModal
        open={contraceptionModalOpen}
        onClose={() => setContraceptionModalOpen(false)}
        userId={userId}
        existingContraceptions={stats?.user?.contraceptions || []}
        healthResponses={healthResponses}
      />

      <EditSurgeryModal
        open={surgeryModalOpen}
        onClose={() => setSurgeryModalOpen(false)}
        userId={userId}
        healthResponses={healthResponses}
      />
    </>
  );
}
